import { useState, useEffect, useCallback } from 'react';
import { tipoServicioAlimentacionApi } from '../api/tipoServicioAlimentacionApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de tipos de servicio de alimentación
 * Proporciona estado y funciones para operaciones CRUD de tipos de servicio de alimentación
 */
export const useTipoServiciosAlimentacion = () => {
  const [tipoServiciosAlimentacion, setTipoServiciosAlimentacion] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtiene todos los tipos de servicio de alimentación
   */
  const fetchTipoServiciosAlimentacion = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoServicioAlimentacionApi.getAll();
      setTipoServiciosAlimentacion(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar tipos de servicio de alimentación');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un tipo de servicio de alimentación específico por ID
   */
  const fetchTipoServicioAlimentacionById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoServicioAlimentacionApi.getById(id);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar el tipo de servicio de alimentación');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo tipo de servicio de alimentación
   */
  const createTipoServicioAlimentacion = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoServicioAlimentacionApi.create(data);
      showSuccessToast('Tipo de servicio de alimentación creado exitosamente');
      
      // Refrescar la lista
      await fetchTipoServiciosAlimentacion();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al crear el tipo de servicio de alimentación');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoServiciosAlimentacion]);

  /**
   * Actualiza un tipo de servicio de alimentación existente
   */
  const updateTipoServicioAlimentacion = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoServicioAlimentacionApi.update(id, data);
      showSuccessToast('Tipo de servicio de alimentación actualizado exitosamente');
      
      // Refrescar la lista
      await fetchTipoServiciosAlimentacion();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el tipo de servicio de alimentación');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoServiciosAlimentacion]);

  /**
   * Elimina un tipo de servicio de alimentación
   */
  const deleteTipoServicioAlimentacion = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await tipoServicioAlimentacionApi.delete(id);
      showSuccessToast('Tipo de servicio de alimentación eliminado exitosamente');
      
      // Refrescar la lista
      await fetchTipoServiciosAlimentacion();
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el tipo de servicio de alimentación');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoServiciosAlimentacion]);

  const fetchUsosTipoServicioAlimentacion = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await tipoServicioAlimentacionApi.getUsos(id);
      return response.data?.data || response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar usos del tipo de servicio');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const unificarTipoServicioAlimentacion = useCallback(async (idOrigen, idDestino) => {
    setLoading(true);
    setError(null);
    try {
      const response = await tipoServicioAlimentacionApi.unificar({
        id_origen: idOrigen,
        id_destino: idDestino,
      });
      showSuccessToast(response.data?.message || 'Tipos unificados correctamente');
      await fetchTipoServiciosAlimentacion();
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al unificar tipos de servicio');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoServiciosAlimentacion]);

  // NOTA: No se cargan automáticamente los tipos de servicio de alimentación
  // Se cargan bajo demanda cuando se necesiten en los componentes

  return {
    // Estado
    tipoServiciosAlimentacion,
    loading,
    error,
    
    // Funciones
    fetchTipoServiciosAlimentacion,
    fetchTipoServicioAlimentacionById,
    createTipoServicioAlimentacion,
    updateTipoServicioAlimentacion,
    deleteTipoServicioAlimentacion,
    fetchUsosTipoServicioAlimentacion,
    unificarTipoServicioAlimentacion,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useTipoServiciosAlimentacion;
